// iter620 — Relaxation Plate Modal.
// Shows the curated anti-cortisol foods (Сьомга, спанак, тиквени семки, матча...)
// with macros + rationale. User can pick which foods to log to today's diary.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";

export default function RelaxationPlateModal({ open, onClose, onLogged }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [plate, setPlate] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [logging, setLogging] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/stress-twin/relaxation-plate`, { timeout: 15000 })
      .then(({ data }) => {
        setPlate(data.plate);
        setSelected(new Set(data.plate.foods.map((_, i) => i)));
        setLoading(false);
      })
      .catch(() => { toast.error("Неуспешно зареждане на чинията."); setLoading(false); });
  }, [open, API]);

  if (!open) return null;

  const toggle = (i) => {
    const next = new Set(selected);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    setSelected(next);
  };

  const logPlate = async () => {
    if (selected.size === 0) {
      toast.error("Избери поне една храна.");
      return;
    }
    setLogging(true);
    try {
      const { data } = await axios.post(`${API}/stress-twin/relaxation-plate/log`, {
        selected_indices: Array.from(selected),
      });
      toast.success(`Записахме ${data.logged} ${data.logged === 1 ? "храна" : "храни"} в дневника ти.`);
      if (onLogged) onLogged(data);
      onClose();
    } catch {
      toast.error("Неуспешно записване. Опитай отново.");
    } finally {
      setLogging(false);
    }
  };

  const dialogCls = isDark
    ? "bg-[hsl(222,47%,9%)] border-white/10 text-white"
    : "bg-white border-gray-200 text-gray-900";

  return (
    <div
      data-testid="relaxation-plate-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-3xl border shadow-2xl ${dialogCls}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2" data-testid="relaxation-plate-title">
                <span aria-hidden="true">🍽️</span> Relaxation чиния
              </h2>
              <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Active Recovery Coach · подбрано за днешния ти кортизолен профил
              </p>
            </div>
            <button
              type="button"
              data-testid="relaxation-plate-close"
              onClick={onClose}
              className={`p-2 rounded-full ${isDark ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
              aria-label="Затвори"
            >
              ✕
            </button>
          </div>

          {loading && (
            <p className={`py-8 text-center text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              Зареждаме чинията…
            </p>
          )}

          {!loading && plate && (
            <>
              <p className={`text-sm leading-relaxed mb-4 ${isDark ? "text-gray-300" : "text-gray-700"}`} data-testid="relaxation-plate-rationale">
                {plate.rationale_bg}
              </p>

              <ul className="space-y-2 mb-4" data-testid="relaxation-plate-foods">
                {plate.foods.map((f, i) => {
                  const isSel = selected.has(i);
                  return (
                    <li
                      key={i}
                      data-testid={`relaxation-plate-food-${i}`}
                      className={`p-3 rounded-xl border transition-colors cursor-pointer ${
                        isSel
                          ? (isDark
                              ? "border-emerald-500/50 bg-emerald-500/10"
                              : "border-emerald-400 bg-emerald-50")
                          : (isDark ? "border-white/10" : "border-gray-200")
                      }`}
                      onClick={() => toggle(i)}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggle(i)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1 accent-emerald-500"
                          data-testid={`relaxation-plate-food-${i}-checkbox`}
                          aria-label={f.food_name}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="font-bold text-sm">{f.food_name}</span>
                            <span className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                              {f.amount_g} g · {f.meal_type === "breakfast" ? "закуска" :
                                f.meal_type === "lunch" ? "обяд" :
                                f.meal_type === "dinner" ? "вечеря" : "снак"}
                            </span>
                          </div>
                          <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                            {f.why_bg}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div
                className={`rounded-xl p-3 mb-4 text-xs flex flex-wrap gap-x-4 gap-y-1 ${
                  isDark ? "bg-white/5" : "bg-gray-50"
                }`}
                data-testid="relaxation-plate-totals"
              >
                <span><strong>{plate.totals.kcal}</strong> kcal</span>
                <span>Б: <strong>{plate.totals.protein_g}</strong> g</span>
                <span>В: <strong>{plate.totals.carbs_g}</strong> g</span>
                <span>М: <strong>{plate.totals.fat_g}</strong> g</span>
                <span>Фибри: <strong>{plate.totals.fiber_g}</strong> g</span>
                <span className="text-emerald-500">≈ DIL {plate.estimated_dil_g}g (нисък)</span>
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  data-testid="relaxation-plate-cancel"
                  onClick={onClose}
                  disabled={logging}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    isDark ? "text-gray-300 hover:bg-white/5" : "text-gray-700 hover:bg-gray-100"
                  } disabled:opacity-50`}
                >
                  Затвори
                </button>
                <button
                  type="button"
                  data-testid="relaxation-plate-log-btn"
                  onClick={logPlate}
                  disabled={logging || selected.size === 0}
                  className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${
                    isDark
                      ? "bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30"
                      : "bg-emerald-500 text-white hover:bg-emerald-600"
                  } disabled:opacity-50`}
                >
                  {logging ? "Записваме…" : `✓ Запиши в дневника (${selected.size})`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
